// "Ticket aktualisieren" (owner decision 2026-10-19): on a row of the current
// revision that is an issue the meeting already filed ("Schon angelegt als
// #N"), owner page only. The click asks the server for a preview first: the
// issue's body as it is now next to the whole body the update writes, with
// a warning when the ticket was changed since Loom wrote it ("Das Ticket
// wurde seit dem Anlegen geändert. …", with who and when when the project's
// record says it: "…, zuletzt von <Name> am <Datum>. …"), or when Loom
// cannot tell, and beside it one more when the ticket is closed (done, or a
// turned-down proposal: "Das Ticket ist erledigt und geschlossen. …"; owner
// decision 2026-10-19, "Show it, with a warning"), or open but already built
// (a done or release-ready status, merged code; owner decision 2026-10-20);
// the update stays allowed. The new body is the old one with the new
// evaluation added (owner 2026-10-30: an update never drops a section): the
// preview lists, collapsed, what stays that the new evaluation does not say
// ("Bleibt erhalten"), how much is new, what comes back from the filing
// ("Wiederhergestellt"), what goes from Loom's own text ("Entfernt (gehört
// zu #N)", "Entfernt (widerspricht)", …), what would go were the ticket not
// changed, the values that belong to another issue ("Gehört zu #N"), and a
// class the new evaluation sees otherwise ("Neu als Epic eingestuft"; the
// labels stay). Only "Aktualisieren" writes, sending the hash of the body
// the preview showed; the server refuses it when the ticket changed since
// (`changed_since_preview`, "Das hat sich inzwischen geändert.").

import { useState } from "react";

import type {
  MeetingIssueRowDto,
  MeetingTicketUpdateMergeDto,
  MeetingTicketUpdatePreviewDto,
  TicketUpdateRetractReason,
} from "@loom/shared/meetings";

import { useFormatDate } from "../../i18n/format.js";
import { usePlural, useT, type MessageKey } from "../../i18n/index.js";

import { failureOf } from "./meetingApi";
import { failureKeyOf } from "./meetingCopy";

/** When the description was last changed: day and time. */
const EDITED_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" };

type Phase =
  | { readonly kind: "closed" }
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly preview: MeetingTicketUpdatePreviewDto }
  | { readonly kind: "error"; readonly key: MessageKey };

/** The heading of what the merge takes out of Loom's own text, by why (`belongs_to` is per issue). */
const RETRACT_KEY: Readonly<Record<Exclude<TicketUpdateRetractReason, "belongs_to">, MessageKey>> = {
  judge_no_contradiction: "meetings.update.merge.contradicts",
  judge_one_subject: "meetings.update.merge.oneSubject",
  judge_out_of_scope: "meetings.update.merge.outOfScope",
  idea_lead: "meetings.update.merge.ideaLead",
};

/** One collapsed list of the merge: its heading, its texts. Nothing when empty. */
function MergeList({ id, label, texts }: { readonly id: string; readonly label: string; readonly texts: readonly string[] }) {
  if (texts.length === 0) return null;
  return (
    <details className="meet-update-list" data-merge={id}>
      <summary>{label}</summary>
      <ul>
        {texts.map((text, i) => (
          <li key={i}>{text}</li>
        ))}
      </ul>
    </details>
  );
}

/** What the update does to the body, beside the two bodies. */
export function MergeLists({ merge }: { readonly merge: MeetingTicketUpdateMergeDto }) {
  const t = useT();
  const tn = usePlural();
  const belongs = new Map<number, string[]>();
  for (const r of merge.retracted) {
    if (r.reason === "belongs_to" && r.iid !== undefined) belongs.set(r.iid, [...(belongs.get(r.iid) ?? []), r.text]);
  }
  return (
    <div className="meet-update-merge">
      {merge.classChanged !== undefined && (
        <div className="meet-update-warn" role="status" data-warn="class">
          {t(merge.classChanged === "epic" ? "meetings.update.merge.classEpic" : "meetings.update.merge.classTicket")}
        </div>
      )}
      {merge.added > 0 && (
        <p className="meet-meta" data-merge="added">
          {t("meetings.update.merge.added", { count: merge.added })}
        </p>
      )}
      <MergeList
        id="kept"
        label={t("meetings.update.merge.kept", { count: merge.kept.length })}
        texts={merge.kept.map((k) => k.text)}
      />
      <MergeList
        id="restored"
        label={t("meetings.update.merge.restored", { count: merge.restored.length })}
        texts={merge.restored.map((r) => r.text)}
      />
      {[...belongs].map(([iid, texts]) => (
        <MergeList key={iid} id={`belongs-${String(iid)}`} label={t("meetings.update.merge.belongsTo", { iid })} texts={texts} />
      ))}
      {(Object.keys(RETRACT_KEY) as (keyof typeof RETRACT_KEY)[]).map((reason) => (
        <MergeList
          key={reason}
          id={reason}
          label={t(RETRACT_KEY[reason])}
          texts={merge.retracted.filter((r) => r.reason === reason).map((r) => r.text)}
        />
      ))}
      <MergeList
        id="flagged"
        label={t("meetings.update.merge.flagged", { count: merge.flagged.length })}
        texts={merge.flagged.map((f) => f.text)}
      />
      {merge.scopedOut.map((o) => (
        <p key={o.iid} className="meet-meta" data-merge="scoped">
          {tn("plural.meetingScopedOut", o.count, { iid: o.iid })}
        </p>
      ))}
    </div>
  );
}

export function MeetingTicketUpdate({
  row,
  disabled,
  onPreview,
  onConfirm,
}: {
  readonly row: MeetingIssueRowDto;
  /** Another write runs on the page. */
  readonly disabled: boolean;
  readonly onPreview: (row: MeetingIssueRowDto) => Promise<MeetingTicketUpdatePreviewDto>;
  /** Resolves null when the update went through, else the line to show. */
  readonly onConfirm: (row: MeetingIssueRowDto, currentBodySha256: string) => Promise<MessageKey | null>;
}) {
  const t = useT();
  const editedWhen = useFormatDate(EDITED_FORMAT);
  const [phase, setPhase] = useState<Phase>({ kind: "closed" });
  const [writing, setWriting] = useState(false);

  const open = (): void => {
    setPhase({ kind: "loading" });
    onPreview(row)
      .then((preview) => setPhase({ kind: "ready", preview }))
      .catch((err: unknown) => setPhase({ kind: "error", key: failureKeyOf(failureOf(err)) }));
  };

  const confirm = (preview: MeetingTicketUpdatePreviewDto): void => {
    setWriting(true);
    onConfirm(row, preview.currentBodySha256)
      .then((key) => setPhase(key === null ? { kind: "closed" } : { kind: "error", key }))
      .finally(() => setWriting(false));
  };

  if (phase.kind === "closed" || phase.kind === "error") {
    return (
      <>
        <button type="button" className="focus-btn is-ghost" disabled={disabled} onClick={open}>
          {t("meetings.update.action")}
        </button>
        {phase.kind === "error" && (
          <div className="focus-error" role="alert">
            {t(phase.key)}
          </div>
        )}
      </>
    );
  }
  if (phase.kind === "loading") {
    return (
      <p className="meet-meta" role="status">
        {t("meetings.update.loading")}
      </p>
    );
  }
  const { preview } = phase;
  const editedBy = preview.edited === "edited" ? (preview.editedBy ?? null) : null;
  const warning: MessageKey | null = preview.same
    ? "meetings.update.same"
    : editedBy !== null
      ? editedBy.name !== null
        ? "meetings.update.editedBy"
        : "meetings.update.editedAt"
      : preview.edited === "edited"
        ? "meetings.update.edited"
        : preview.edited === "unknown"
          ? "meetings.update.unknown"
          : null;
  const closed: MessageKey | null =
    preview.issueStatus === "done"
      ? "meetings.update.closedDone"
      : preview.issueStatus === "declined"
        ? "meetings.update.closedDeclined"
        : null;
  // Built already, while still open: the closed warning says it otherwise.
  const done: MessageKey | null =
    closed !== null
      ? null
      : preview.done === "released_label"
        ? "meetings.update.doneLabel"
        : preview.done === "merged_change"
          ? "meetings.update.doneMerged"
          : null;
  return (
    <div className="meet-update" data-update={row.ticketId}>
      <p className="meet-meta">
        {t((preview.merge?.retracted.length ?? 0) > 0 ? "meetings.update.hintRetracts" : "meetings.update.hint")}
      </p>
      {closed !== null && (
        <div className="meet-update-warn" role="alert" data-warn="closed">
          {t(closed)}
        </div>
      )}
      {done !== null && (
        <div className="meet-update-warn" role="alert" data-warn="done">
          {t(done)}
        </div>
      )}
      {warning !== null && (
        <div className={preview.same ? "focus-help" : "meet-update-warn"} role={preview.same ? "status" : "alert"}>
          {editedBy !== null
            ? t(warning, { name: editedBy.name ?? "", date: editedWhen(editedBy.at) })
            : t(warning)}
        </div>
      )}
      {preview.merge !== undefined && <MergeLists merge={preview.merge} />}
      <div className="meet-update-bodies">
        <section className="meet-update-body">
          <span className="meet-meta">{t("meetings.update.current")}</span>
          <pre data-body="current">{preview.currentBody}</pre>
        </section>
        <section className="meet-update-body">
          <span className="meet-meta">{t("meetings.update.next")}</span>
          <pre data-body="next">{preview.newBody}</pre>
        </section>
      </div>
      <div className="meet-actions">
        <button
          type="button"
          className="focus-btn"
          disabled={disabled || writing}
          onClick={() => confirm(preview)}
        >
          {t("meetings.update.confirm")}
        </button>
        <button
          type="button"
          className="focus-btn is-ghost"
          disabled={writing}
          onClick={() => setPhase({ kind: "closed" })}
        >
          {t("meetings.update.cancel")}
        </button>
      </div>
    </div>
  );
}
